/**
 * Overview 页顶部「项目 · 资源占用」区块。
 *
 * 手动刷新: `refreshToken` 每变一次触发一次 fetch。失败不清空面板, 只把小圆点
 * 灰掉, 避免闪烁。project 顺序 = 后端 settings.overview.project_ids 顺序;
 * emoji 按 project_id 语义在前端做本地映射, 后端暂不返回 emoji 字段。
 */
import React, { useEffect, useMemo, useState } from "react";
import { Typography, Space } from "antd";
import {
  getOverviewProjectResources,
  type OverviewResourceItem,
  type OverviewResourceResponse,
} from "@/apis/overview";
import ProjectResourceCard from "./ProjectResourceCard";

const { Title } = Typography;

/** project_id → 卡头 emoji 的本地映射,新项目落这里加一行即可。 */
const PROJECT_EMOJI: Record<string, string> = {
  simulation: "🖥",
  "simulation-gpu": "🎮",
};

function emojiFor(projectId: string): string {
  return PROJECT_EMOJI[projectId] ?? "📦";
}

interface Props {
  /** 父级递增的刷新令牌;每变一次触发一次 fetch。 */
  refreshToken: number;
}

const ProjectResourceSection: React.FC<Props> = ({ refreshToken }) => {
  const [data, setData] = useState<OverviewResourceResponse | null>(null);
  const [initialLoading, setInitialLoading] = useState(true);
  const [live, setLive] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const resp = await getOverviewProjectResources();
        if (cancelled) return;
        setData(resp);
        setLive(true);
      } catch {
        // 静默:保留上一次数据,顶部圆点灰掉即可,避免闪烁
        if (!cancelled) setLive(false);
      } finally {
        if (!cancelled) setInitialLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [refreshToken]);

  const items: OverviewResourceItem[] = useMemo(
    () => data?.projects ?? [],
    [data],
  );

  return (
    <section style={{ marginTop: 24 }}>
      <div
        style={{
          display: "flex",
          alignItems: "baseline",
          justifyContent: "space-between",
          margin: "0 4px 12px",
        }}
      >
        <Space size={8} align="baseline">
          <Title level={5} style={{ margin: 0, fontSize: 15, color: "#374151" }}>
            项目 · 资源占用
          </Title>
          <span
            title={live ? "实时数据" : "拉取失败,展示上次数据"}
            style={{
              display: "inline-block",
              width: 8,
              height: 8,
              borderRadius: "50%",
              background: live ? "#16a34a" : "#c4c6cc",
              boxShadow: live ? "0 0 0 4px rgba(22,163,74,.15)" : "none",
              transition: "background .3s",
            }}
          />
        </Space>
        <span style={{ color: "#8c8c8c", fontSize: 12 }}>
          数据源 Compute-Flow
        </span>
      </div>

      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
          gap: 16,
        }}
      >
        {(initialLoading && items.length === 0 ? SKELETON_ITEMS : items).map((it) => (
          <ProjectResourceCard
            key={it.project_id}
            item={it}
            emoji={emojiFor(it.project_id)}
            loading={initialLoading && items.length === 0}
          />
        ))}
      </div>
    </section>
  );
};

// 首次加载 skeleton 用的占位;两张卡对齐 mockup 默认布局。
const SKELETON_ITEMS: OverviewResourceItem[] = [
  {
    project_id: "simulation",
    display_name: "simulation",
    scope: "线上项目",
    resources: {},
    params: {
      max_parallel_subtasks: null,
      tasks_per_worker: null,
      tasks_per_pod: null,
    },
    error: null,
  },
  {
    project_id: "simulation-gpu",
    display_name: "simulation-gpu",
    scope: "线上项目",
    resources: {},
    params: {
      max_parallel_subtasks: null,
      tasks_per_worker: null,
      tasks_per_pod: null,
    },
    error: null,
  },
];

export default ProjectResourceSection;
